'use client';

import React, { createContext, useContext, useEffect } from 'react';
import useSWR from 'swr';

// FIX-18 (PERF-02): centralizes the auth/me/admin-check fetches that used to
// live inside Navbar's pathname-dependent useEffect, which re-fetched all of
// this on every client-side navigation. SWR fetches once on mount, dedupes
// for 30s, and only re-fetches on the 'auth-changed' event (dispatched on
// login/logout elsewhere in the app) - never on pathname change.

type AuthStatusResponse = { authenticated?: boolean };
type MeResponse = { nftAvatarUrl?: string | null; address?: string };
type AdminCheckResponse = { isAdmin?: boolean; address?: string };

type AuthState = {
  authenticated: boolean;
  isAdmin: boolean;
  nftAvatarUrl: string | null;
  // FIX-49: the current user's own wallet address, surfaced alongside
  // nftAvatarUrl so call sites (Navbar, search page) can key a
  // WalletIdenticon fallback off it without each one re-fetching /api/me.
  address: string | null;
  loading: boolean;
  refresh: () => void;
};

const AuthContext = createContext<AuthState>({
  authenticated: false,
  isAdmin: false,
  nftAvatarUrl: null,
  address: null,
  loading: true,
  refresh: () => {},
});

const fetcher = (url: string) =>
  fetch(url, { credentials: 'include', cache: 'no-store' }).then((r) => r.json());

// FIX-29: best-effort silent refresh. The access token (`token` cookie) now
// expires in 15 minutes instead of 1 day; this POSTs the refresh_token cookie
// to mint a new access token before/while it's still valid, so users aren't
// logged out every 15 minutes. Failures are swallowed - if the refresh token
// is missing/expired, /api/auth/status will simply report unauthenticated.
function silentRefresh() {
  return fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' }).catch(() => null);
}

// Comfortably under the 15-minute access token lifetime so the background
// refresh lands before expiry even accounting for tab throttling.
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    data: status,
    isLoading: statusLoading,
    mutate: mutateStatus,
  } = useSWR<AuthStatusResponse>('/api/auth/status', fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 30_000,
  });

  const authenticated = !!status?.authenticated;

  // IMPORTANT: show authenticated state as soon as we know the cookie is
  // valid. /api/me and /api/admin/check can fail independently (e.g. if
  // MongoDB is down); we still want Sign out visible so users can log out
  // and the UI doesn't get stuck.
  const { data: me, mutate: mutateMe } = useSWR<MeResponse>(
    authenticated ? '/api/me' : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 30_000 }
  );

  // Admin status must come from the server (/api/admin/check verifies the
  // JWT session against the server-only ADMIN_WALLETS allowlist). We never
  // read an admin allowlist out of NEXT_PUBLIC_ env vars on the client.
  const { data: adminCheck, mutate: mutateAdmin } = useSWR<AdminCheckResponse>(
    authenticated ? '/api/admin/check' : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 30_000 }
  );

  const refresh = () => {
    mutateStatus();
    mutateMe();
    mutateAdmin();
  };

  // Other parts of the app (login, logout, wallet disconnect) dispatch
  // 'auth-changed' on window when auth state changes out-of-band. Re-fetch
  // everything when that happens instead of polling on every navigation.
  useEffect(() => {
    function onAuthChanged() {
      refresh();
    }
    window.addEventListener('auth-changed', onAuthChanged);
    return () => window.removeEventListener('auth-changed', onAuthChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FIX-29: fire a silent refresh once on mount, before trusting /api/auth/status.
  // This covers the case where a returning user's access token has already
  // expired (tab was closed/idle past 15 minutes) but their refresh token is
  // still valid - without this, they'd show as logged out until next sign-in.
  useEffect(() => {
    silentRefresh().then(() => mutateStatus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While authenticated, keep renewing the access token in the background so
  // a long-lived session (up to the 7-day refresh token lifetime) doesn't get
  // interrupted every 15 minutes.
  useEffect(() => {
    if (!authenticated) return;
    const id = setInterval(() => {
      silentRefresh();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [authenticated]);

  const nftAvatarUrl =
    typeof me?.nftAvatarUrl === 'string' && me.nftAvatarUrl.trim() ? me.nftAvatarUrl : null;
  const address = typeof me?.address === 'string' && me.address.trim() ? me.address : null;

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        isAdmin: authenticated ? Boolean(adminCheck?.isAdmin) : false,
        nftAvatarUrl,
        address,
        loading: statusLoading,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
